import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import '../App.css';
import { auth, db, getCollectionName } from '../firebase';
import { collection, query, where, onSnapshot, doc, getDoc, campos } from '../firestore-compat';
import { format, differenceInMinutes } from 'date-fns';
import ToastNotification from './ToastNotification';
import AvisoEmail from '../email/AvisoEmail';
import { registrarAcesso } from '../utils/registrarAcesso';
import { nomeDaTela } from '../config/telas';
import { signOut } from '../auth-compat';
import { useLogoutPorInatividade, ehTelaDePainel, LIMITE_MIN } from '../hooks/useLogoutPorInatividade';
import { useVisual, useAplicarVidro, FundoVidro } from './Vidro';

const TOASTS_DESATIVADOS = true; // pop-ups desativados temporariamente — sininho/contador de convites continuam normais

// 2026-09-22: essas 7 assinaturas onSnapshot abaixo refaziam a busca completa de
// agendaQualidade/captacao_agendamentos/captacao_pacientes(x2)/ocorrencias_v2(x2)/notificacoes
// a cada ~2min, o dia todo, em toda página, para cada usuário logado — sem corte de data em
// captacao_pacientes (status Implantado) nem em ocorrencias_v2. Foi a causa do estouro de
// egress do Supabase (73% da cota de 250GB consumida no 1º dia do ciclo). Desativado até as
// telas de captação/ocorrências passarem a ter atualização sob demanda (botão + auto-refresh
// a cada 30min só na tela de captação) em vez de polling global no layout.
const NOTIFICACOES_TEMPO_REAL_DESATIVADAS = true;

const MainLayout = ({ children }) => {
    const location = useLocation();
    const isPainel = location.pathname === '/painel-captacao'
        || (location.pathname === '/gestao-captacao' && new URLSearchParams(location.search).get('fullscreen') === '1');
    // INEP tem seu próprio shell completo (sidebar, topo, navegação) — some com o do Quality
    // pra dar a sensação de "outro sistema", em vez de duplicar a barra lateral.
    const isFullApp = location.pathname.startsWith('/inep');

    // Visual "Liquid Glass" escolhido pelo usuário (botão ✨ Visual) — nunca nos painéis das TVs nem no INEP
    const visual = useVisual();
    const vidro = visual.vidro && !isFullApp && !ehTelaDePainel(location);
    useAplicarVidro(vidro, visual.fundo);

    // Logout automático por inatividade (telas de painel ficam de fora).
    const expirarSessao = useCallback(async () => {
        try { await signOut(auth); } catch (e) { /* segue para o login mesmo assim */ }
        window.location.href = '/login';
    }, []);
    // Usuário marcado em Configurações → Usuários como "não desconectar" (login de TV/painel)
    const [semLogoutInatividade, setSemLogoutInatividade] = useState(false);
    const emailLogado = auth.currentUser?.email || '';
    useEffect(() => {
        if (!emailLogado) return undefined;
        const q = query(collection(db, getCollectionName('users')), where('email', '==', emailLogado), campos('semLogoutInatividade'));
        return onSnapshot(q, snap => setSemLogoutInatividade(!snap.empty && snap.docs[0].data().semLogoutInatividade === true), () => {});
    }, [emailLogado]);
    const { segundosRestantes, continuar } = useLogoutPorInatividade({
        ativo: !!auth.currentUser,
        emPainel: ehTelaDePainel(location) || semLogoutInatividade,
        aoExpirar: expirarSessao,
    });
    // histórico de acessos: cada tela aberta (Administração → Histórico de acessos)
    useEffect(() => {
        if (!auth.currentUser) return;
        registrarAcesso({ tipo: 'tela', caminho: location.pathname + location.search, tela: nomeDaTela(location.pathname) });
    }, [location.pathname, location.search]);
    const [sidebarAutoHide, setSidebarAutoHide] = useState(false);
    const toggleSidebar = () => setSidebarAutoHide(v => !v);

    const handleContentClick = () => {
        if (!sidebarAutoHide) setSidebarAutoHide(true);
    };

    // --- NOTIFICAÇÕES DE AUDITORIA ---
    const [notificacao, setNotificacao] = useState(null);
    const notifiedRef = useRef(new Set()); // Para não notificar repetidamente o mesmo evento

    // Rastreamento de IDs já vistos (para detectar novos docs no compat polling)
    const seenAgRef         = useRef(null); // null = primeira carga ainda não processada
    const seenImplRef       = useRef(null);
    const seenImplantadoRef = useRef(null);
    const seenNotifRef      = useRef(null);
    // Rastreamento de status de ocorrências (id → status) para detectar mudanças
    const ocStatusRef = useRef(null); // null = primeira carga ainda não processada

    useEffect(() => {
        if (NOTIFICACOES_TEMPO_REAL_DESATIVADAS) return;
        const todayStr = format(new Date(), 'yyyy-MM-dd');
        const q = query(
            collection(db, 'agendaQualidade'),
            where('data', '==', todayStr)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const eventosHoje = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Função de verificação
            const checkUpcoming = () => {
                const now = new Date();
                eventosHoje.forEach(evento => {
                    // Pula se já notificado
                    if (notifiedRef.current.has(evento.id)) return;

                    // Parse do horário (HH:mm)
                    const [horas, minutos] = evento.horario.split(':').map(Number);
                    const eventDate = new Date();
                    eventDate.setHours(horas, minutos, 0, 0);

                    const diff = differenceInMinutes(eventDate, now);

                    // Notificar se faltar entre 1 e 30 minutos
                    if (diff > 0 && diff <= 30) {
                        setNotificacao({
                            message: `⏰ Atenção! Evento às ${evento.horario} (${evento.descricao}) em ${diff} min.`,
                            type: 'warning'
                        });
                        notifiedRef.current.add(evento.id);

                        // Tentar notificação do navegador também
                        if (Notification.permission === "granted") {
                            new Notification("Quality: Compromisso Próximo", {
                                body: `${evento.descricao} às ${evento.horario} `
                            });
                        } else if (Notification.permission !== "denied") {
                            Notification.requestPermission();
                        }
                    }
                });
            };

            // Verificar agora e a cada minuto
            checkUpcoming();
            const interval = setInterval(checkUpcoming, 60000);
            return () => clearInterval(interval);
        });

        // Pedir permissão ao carregar
        if (Notification.permission !== "granted" && Notification.permission !== "denied") {
            Notification.requestPermission();
        }

        return () => unsubscribe();
    }, []);

    // --- NOTIFICAÇÕES DE CAPTAÇÃO ---
    useEffect(() => {
        if (NOTIFICACOES_TEMPO_REAL_DESATIVADAS) return;
        const user = auth.currentUser;
        if (!user?.email) return;

        const unsubs = [];

        function dispararNotif(titulo, corpo, tipo) {
            setNotificacao({ message: corpo, type: tipo });
            if (Notification.permission === 'granted') {
                new Notification(titulo, { body: corpo, icon: '/logo192.png' });
            } else if (Notification.permission !== 'denied') {
                Notification.requestPermission().then(p => {
                    if (p === 'granted') new Notification(titulo, { body: corpo, icon: '/logo192.png' });
                });
            }
        }

        // 1. Novos agendamentos de captação para este profissional
        const qAg = query(
            collection(db, getCollectionName('captacao_agendamentos')),
            where('profissionalEmail', '==', user.email)
        );
        unsubs.push(onSnapshot(qAg, (snap) => {
            const currentIds = new Set(snap.docs.map(d => d.id));
            if (seenAgRef.current === null) {
                seenAgRef.current = currentIds;
                return;
            }
            snap.docs.forEach(d => {
                if (!seenAgRef.current.has(d.id)) {
                    const ag = d.data();
                    if (ag.status === 'agendado') {
                        const tipo = ag.tipoAgenda === 'implantacao' ? 'Implantação' : 'Avaliação';
                        const dataFmt = ag.data ? ag.data.split('-').reverse().join('/') : '';
                        const horario = ag.hora ? ` às ${ag.hora}` : '';
                        dispararNotif(
                            `📅 Novo agendamento — ${tipo}`,
                            `${ag.pacienteNome || 'Paciente'} em ${dataFmt}${horario}`,
                            'info'
                        );
                    }
                }
            });
            seenAgRef.current = currentIds;
        }, () => {}));

        // 2. Paciente entrando em "Em Implantação" — somente para admin/coordenador
        getDoc(doc(db, getCollectionName('usuarios_v2'), user.uid)).then(userSnap => {
            if (!userSnap.exists()) return;
            const roles = Array.isArray(userSnap.data().roles)
                ? userSnap.data().roles
                : (userSnap.data().role ? [userSnap.data().role] : []);
            const temAcesso = roles.some(r => ['admin','administrador','coordenador','captacao'].includes(r));
            if (!temAcesso) return;

            const qImpl = query(
                collection(db, getCollectionName('captacao_pacientes')),
                where('status', '==', 'Em Implantação'),
                campos('nome', 'convenio') // só o que o aviso mostra
            );
            unsubs.push(onSnapshot(qImpl, (snap) => {
                const currentIds = new Set(snap.docs.map(d => d.id));
                if (seenImplRef.current === null) {
                    seenImplRef.current = currentIds;
                    return;
                }
                snap.docs.forEach(d => {
                    if (!seenImplRef.current.has(d.id)) {
                        const p = d.data();
                        dispararNotif(
                            '🔧 Paciente em Implantação',
                            `${p.nome || 'Paciente'}${p.convenio ? ' — ' + p.convenio : ''}`,
                            'warning'
                        );
                    }
                });
                seenImplRef.current = currentIds;
            }, () => {}));
        }).catch(() => {});

        // 3. Paciente entrando em "Implantado" — supervisor de núcleo
        const qImplantado = query(
            collection(db, getCollectionName('captacao_pacientes')),
            where('status', '==', 'Implantado'),
            campos('nome', 'convenio') // só o que o aviso mostra (o paciente inteiro tinha ~75 MB no total)
        );
        unsubs.push(onSnapshot(qImplantado, (snap) => {
            const currentIds = new Set(snap.docs.map(d => d.id));
            if (seenImplantadoRef.current === null) {
                seenImplantadoRef.current = currentIds;
                return;
            }
            snap.docs.forEach(d => {
                if (!seenImplantadoRef.current.has(d.id)) {
                    const p = d.data();
                    dispararNotif(
                        '✅ Paciente Implantado',
                        `${p.nome || 'Paciente'}${p.convenio ? ' — ' + p.convenio : ''} foi implantado. Verifique o Painel Implantados.`,
                        'success'
                    );
                }
            });
            seenImplantadoRef.current = currentIds;
        }, () => {}));

        return () => unsubs.forEach(fn => fn());
    }, []); // eslint-disable-line

    // --- NOTIFICAÇÕES DE MUDANÇA DE STATUS DE OCORRÊNCIAS ---
    useEffect(() => {
        if (NOTIFICACOES_TEMPO_REAL_DESATIVADAS) return;
        const user = auth.currentUser;
        if (!user?.uid) return;

        const unsubs = [];

        const STATUS_LABEL = {
            aberto: 'Aberto',
            em_andamento: 'Em andamento',
            concluido: 'Concluído',
            cancelado: 'Cancelado',
        };

        function notificarMudancaStatus(oc, statusAntigo, statusNovo) {
            const pacNome = oc.paciente?.nomeCompleto || oc.paciente?.nome || 'Paciente';
            const de = STATUS_LABEL[statusAntigo] || statusAntigo;
            const para = STATUS_LABEL[statusNovo] || statusNovo;
            const corpo = `${pacNome}: ${de} → ${para}`;
            const titulo = '📋 Ocorrência atualizada';
            setNotificacao({ message: `📋 ${corpo}`, type: statusNovo === 'concluido' ? 'success' : 'info' });
            if (Notification.permission === 'granted') {
                new Notification(titulo, { body: corpo, icon: '/logo192.png' });
            }
        }

        function processarSnap(snap) {
            if (ocStatusRef.current === null) {
                // Primeira carga — só registra os status atuais
                const mapa = {};
                snap.docs.forEach(d => { mapa[d.id] = d.data().status; });
                ocStatusRef.current = mapa;
                return;
            }
            snap.docs.forEach(d => {
                const statusNovo = d.data().status;
                const statusAntigo = ocStatusRef.current[d.id];
                if (statusAntigo !== undefined && statusAntigo !== statusNovo) {
                    notificarMudancaStatus({ id: d.id, ...d.data() }, statusAntigo, statusNovo);
                }
                ocStatusRef.current[d.id] = statusNovo;
            });
        }

        // Query 1: ocorrências criadas por este usuário
        const qCriador = query(
            collection(db, getCollectionName('ocorrencias_v2')),
            where('criadoPorUid', '==', user.uid)
        );
        unsubs.push(onSnapshot(qCriador, processarSnap, () => {}));

        // Query 2: ocorrências onde este usuário é responsável por tarefa
        const qResponsavel = query(
            collection(db, getCollectionName('ocorrencias_v2')),
            where('responsaveisUids', 'array-contains', user.uid)
        );
        unsubs.push(onSnapshot(qResponsavel, processarSnap, () => {}));

        return () => unsubs.forEach(fn => fn());
    }, []); // eslint-disable-line

    // --- NOTIFICAÇÕES DIRETAS (captacao_agendamentos → notificacoes) ---
    useEffect(() => {
        if (NOTIFICACOES_TEMPO_REAL_DESATIVADAS) return;
        const user = auth.currentUser;
        if (!user?.email) return;
        const qNotif = query(
            collection(db, getCollectionName('notificacoes')),
            where('para', '==', user.email)
        );
        const unsub = onSnapshot(qNotif, (snap) => {
            const currentIds = new Set(snap.docs.map(d => d.id));
            if (seenNotifRef.current === null) {
                seenNotifRef.current = currentIds;
                return;
            }
            snap.docs.forEach(d => {
                if (!seenNotifRef.current.has(d.id)) {
                    const n = d.data();
                    setNotificacao({ message: `${n.titulo}: ${n.corpo}`, type: 'info' });
                    if (Notification.permission === 'granted') {
                        new Notification(n.titulo, { body: n.corpo, icon: '/logo192.png' });
                    }
                }
            });
            seenNotifRef.current = currentIds;
        }, () => {});
        return () => unsub();
    }, []); // eslint-disable-line

    return (
        <div className="app-container">
            {vidro && <FundoVidro />}
            {!isFullApp && <Sidebar autoHide={sidebarAutoHide} />}

            <div className={`main-content ${(sidebarAutoHide || isFullApp) ? 'no-sidebar' : ''}`} onClick={handleContentClick}>
                {!isPainel && !isFullApp && <TopBar toggleSidebar={toggleSidebar} />}
                {/* aviso de e-mail novo em qualquer tela (só para quem tem a tela de E-mail) */}
                {!isPainel && !isFullApp && <AvisoEmail />}
                <div className="content-area" style={{ padding: 0 }}>
                    {children}
                </div>
                {!isPainel && !isFullApp && (
                    <footer className="rodape-versao" style={{
                        padding: '10px 20px',
                        fontSize: '0.7rem',
                        color: '#999',
                        textAlign: 'right',
                        borderTop: '1px solid #eee',
                        background: '#fff',
                        marginTop: 'auto'
                    }}>
                        Versão 5.0 · Desenvolvido por Alexandre Oliveira - ADM Soluções Criativas 2025-2026 · Certificado INPI
                    </footer>
                )}
            </div>

            {segundosRestantes !== null && (
                <div role="alertdialog" aria-labelledby="aviso-inatividade-titulo"
                    style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
                    <div style={{ background: '#fff', borderRadius: 16, padding: '24px 28px', maxWidth: 420, width: '100%', boxShadow: '0 20px 50px rgba(0,0,0,0.25)', textAlign: 'center' }}>
                        <div id="aviso-inatividade-titulo" style={{ fontSize: '1.15rem', fontWeight: 800, color: '#1e293b', marginBottom: 8 }}>Você ainda está aí?</div>
                        <div style={{ fontSize: '0.92rem', color: '#475569', marginBottom: 18 }}>
                            Por segurança, o sistema desconecta após {LIMITE_MIN} minutos sem uso.<br />
                            Você será desconectado em <strong>{segundosRestantes} segundos</strong>.
                        </div>
                        <button onClick={continuar} autoFocus
                            style={{ background: '#4f46e5', color: '#fff', border: 0, borderRadius: 10, padding: '11px 22px', fontSize: '0.92rem', fontWeight: 700, cursor: 'pointer' }}>
                            Continuar conectado
                        </button>
                    </div>
                </div>
            )}

            {!TOASTS_DESATIVADOS && notificacao && (
                <ToastNotification
                    message={notificacao.message}
                    type={notificacao.type}
                    onClose={() => setNotificacao(null)}
                />
            )}
        </div>
    );
};

export default MainLayout;
